<template>
  <BaseLayout>
    <template #drawer-left>
      <Sidebar>
        <v-list>
          <BackListItem
            v-if="meeting"
            :title="$t('Back_to_meeting')"
            :parent="meeting"
            :subtitle="toLocaleDateTimeString(meeting.start)"
          />
        </v-list>
      </Sidebar>
    </template>
    <v-card :style="{ '--table-width': `${tableWidth}px` }">
      <v-card-title>
        <h1 class="headline">{{ $t('advanceCard') }}</h1>
      </v-card-title>
      <v-card-text>
        <div class="d-flex align-center mb-6">
          <v-autocomplete
            v-model="filter.business_type"
            :items="businessTypes"
            :label="$t('Business_type')"
            clearable
            outlined
            dense
            class="mr-4"
            hide-details
            style="max-width: 300px"
          />
          <v-checkbox v-model="filter.can_sign" class="ma-0" hide-details :label="$t('advanceCanBeSigned')" />
          <v-checkbox v-model="filter.has_signed" class="ma-0" hide-details :label="$t('advanceHasSigned')" />
        </div>
        <v-divider />
        <ftw-serverside-collection
          ref="table"
          data-testid="advances-table"
          :fetch="fetchAdvances"
          page-size-param="page_size"
          :filter.sync="filter"
          :headers.sync="tableHeaders"
          item-key="id"
          :items-per-page-default="10"
          :items-per-page-options="[10, 20, 50]"
          route-replace
        >
          <template #item="{ item }">
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  <span
                    class="text-underline blue--text cursor-pointer advance-title-link"
                    data-testid="advance-title"
                    @click="openDetailDialog(item)"
                  >
                    {{ item.title }}
                  </span>
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ firstSignatoryLabel(item) }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <AdvanceMenu
                  :user="user"
                  :meeting="currentMeeting"
                  :advance="item"
                  :can-sign="canSign(item)"
                  @edit="openEditDialog(item)"
                  @sign="openSignDialog(item)"
                  @submit="openSubmitDialog(item)"
                />
              </v-list-item-action>
            </v-list-item>
          </template>
          <template #item.title="{ item }">
            <div class="advance-title-content">
              <span
                class="text-underline blue--text cursor-pointer advance-title-link"
                data-testid="advance-title"
                @click="openDetailDialog(item)"
              >
                {{ item.title }}
              </span>
            </div>
          </template>
          <template #item.type="{ item }">
            {{ item.business_type.text }}
          </template>
          <template #item.creator="{ item }">
            {{ firstSignatoryLabel(item) }}
          </template>
          <template #item.count="{ item }">
            <span data-testid="advance-signed-signatories-count">
              {{
                $t('advancesTableSignatoriesDisplay', {
                  signedCount: item.signed_all_signatories_count,
                  totalCount: item.all_signatories_count,
                })
              }}
            </span>
          </template>
          <template #item.status="{ item }">
            <v-chip :color="statusChipColor(item)" data-testid="advance-status">{{ item.status_display }}</v-chip>
            <template v-if="!meeting.is_running_meeting && !isSubmitted(item)">
              <v-tooltip top>
                <template #activator="{ on: on, attrs: attrs }">
                  <v-icon class="ml-3" color="yellow" v-bind="attrs" v-on="on">mdi-alert</v-icon>
                </template>
                <span>{{ $t('meetingIsNotRunning') }}</span>
              </v-tooltip>
            </template>
          </template>
          <template #item.menu="{ item }">
            <AdvanceMenu
              :user="user"
              :meeting="currentMeeting"
              :advance="item"
              :can-sign="canSign(item)"
              @published="refresh"
              @destroyed="refresh"
              @edit="openEditDialog(item)"
              @sign="openSignDialog(item)"
              @submit="openSubmitDialog(item)"
            />
          </template>
        </ftw-serverside-collection>
      </v-card-text>
    </v-card>
    <AdvanceDialog ref="advanceDialog" @update="refresh" />
    <AdvanceSignDialog ref="advanceSignDialog" @signed="refresh" />
    <AdvanceDetailDialog ref="advanceDetailDialog" />
    <AdvanceSubmitDialog ref="advanceSubmitDialog" @submitted="refresh" />
  </BaseLayout>
</template>

<script>
import { createLink } from '@/api/helpers'
import { api } from '@/api'
import Profile from '@/store/models/profile'
import User from '@/store/models/user'
import get from 'lodash/get'
import Meeting from '@/store/models/meeting'
import Committee from '@/store/models/committee'
import { toLocaleDateTimeString } from '@/filters'
import { fromQueryString } from '@4tw/ui/lib/util/query'
import castArray from 'lodash/castArray'

const statusChipColors = {
  DRAFT: 'error',
  OPEN: 'warning',
  SUBMITTED: 'success',
}

export default {
  name: 'Advances',
  data() {
    return {
      businessTypes: [],
      currentMeeting: {},
      filter: fromQueryString(
        this.$route.query,
        ['business_type', 'can_sign', 'has_signed', 'ordering'],
        {
          business_type: null,
          can_sign: false,
          has_signed: false,
          ordering: [],
        },
        {
          can_sign: (value) => value === true || value === 'true',
          has_signed: (value) => value === true || value === 'true',
          ordering: castArray,
        },
      ),
      tableHeaders: [
        {
          text: this.$t('advancesTableTitle'),
          value: 'title',
        },
        {
          text: this.$t('advancesTableBusinessType'),
          value: 'type',
        },
        {
          text: this.$t('Business_case_advance_creator'),
          value: 'creator',
        },
        {
          text: this.$t('advancesTableSignatoriesCount'),
          value: 'count',
        },
        {
          text: this.$t('advancesTableStauts'),
          value: 'status',
        },
        {
          value: 'menu',
        },
      ],
      resizeObserver: null,
      tableWidth: 0,
    }
  },
  computed: {
    meetingId() {
      return this.$route.params.id
    },
    meeting() {
      return Meeting.query().find(this.meetingId)
    },
    user() {
      return Profile.query().first()
    },
  },
  async mounted() {
    const meeting = await Meeting.findOrFetch(this.meetingId)
    const committee = await Committee.findOrFetch(meeting.committee_id)
    this.currentMeeting = meeting
    User.fetchAllByModel(committee)
    this.refresh()
    this.fetchBusinessTypes()
    this.setTableWidth()
  },
  methods: {
    toLocaleDateTimeString,
    refresh() {
      this.$refs.table.update()
    },
    async fetchAdvances(params) {
      const { data } = await api.get(
        createLink('meetings/{id}/advances', {
          id: this.meetingId,
        }),
        {
          params,
        },
      )
      return data
    },
    async fetchBusinessTypes() {
      const businessTypes = await api.get(createLink('businesstypes'), { params: { show_for_advance: true } })
      this.businessTypes = businessTypes.data.map((businessType) => ({
        text: businessType.title,
        value: businessType.api_uid,
      }))
    },
    firstSignatoryLabel(advance) {
      const firstSignatory = advance.first_signatory
      return (
        [firstSignatory.first_name, firstSignatory.last_name].filter(Boolean).join(' ') +
        ` (${firstSignatory.username})`
      )
    },
    isOpen(advance) {
      return advance.status === 'OPEN'
    },
    isSubmitted(advance) {
      return advance.status === 'SUBMITTED'
    },
    canSign(advance) {
      return advance.can_sign && this.currentMeeting.is_running_meeting
    },
    openEditDialog(advance) {
      this.$refs.advanceDialog.openWithAdvance(advance)
    },
    openSignDialog(advance) {
      this.$refs.advanceSignDialog.open(advance)
    },
    openDetailDialog(advance) {
      this.$refs.advanceDetailDialog.open(advance)
    },
    openSubmitDialog(advance) {
      this.$refs.advanceSubmitDialog.open(advance)
    },
    statusChipColor(advance) {
      return get(statusChipColors, advance.status, 'grey')
    },
    setTableWidth() {
      this.$nextTick(() => {
        const el = this.$refs.table?.$el
        if (!el) return

        this.tableWidth = el.offsetWidth

        this.resizeObserver = new ResizeObserver(([entry]) => {
          this.tableWidth = entry.contentRect.width
        })

        this.resizeObserver.observe(el)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.advance-title-content {
  max-width: calc(var(--table-width) / 2);
  min-width: 250px;
}

.advance-title-link {
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
